/**
 * Analysis types — mirror of Pydantic schemas in backend/sherlock/schemas/analysis.py.
 * Wire format is snake_case throughout.
 */

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type AnalysisAction = 'do_now' | 'investigate' | 'watch' | 'wontfix';
// Opus disabled for now; re-add 'claude-opus-4-7' here AND in MODEL_PRICING below
// to reactivate Deep Dive UI.
export type AnalysisModel = 'claude-sonnet-4-6';
export type PromptVersion = 'auto_triage.v1' | 'auto_triage_agentic.v1';

/**
 * A single tool invocation recorded during an agentic analysis run.
 * Mirrors ToolCallEntry in backend/sherlock/schemas/analysis.py.
 */
export interface ToolCallEntry {
  round: number;
  tool: string;           // e.g. 'github_read_file'
  args: Record<string, unknown>;
  result_preview: string; // full JSON-serialized tool result (no byte cap; render via prettyJson)
  latency_ms: number;
  error: string | null;
}

/**
 * Sprint 5 — structured tool outputs captured during the agentic loop.
 * Keys mirror those set by services/analysis.py:_capture_for_context.
 * Absent keys mean the LLM didn't invoke that tool for this issue.
 */
export interface TraceSpan {
  depth: number;
  service: string | null;
  resource: string | null;
  duration_ms: number | null;
  start_ms_offset: number | null;
  type: string | null;
  http_status_code: number | null;
  error: { type: string | null; message: string | null; stack: string | null } | null;
  span_id: string;
  parent_id: string | null;
}

export interface TraceDetail {
  trace_id: string;
  duration_ms: number | null;
  span_count: number;
  truncated_spans: number;
  root: {
    service: string | null;
    resource: string | null;
    http_status_code: number | null;
    duration_ms: number | null;
    started_at: string | null;
  } | null;
  error_span: TraceSpan | null;
  sql_queries: { statement: string; duration_ms: number | null; service: string | null; depth: number }[];
  spans: TraceSpan[];
}

export interface RecentEvent {
  title: string | null;
  text: string;
  source: string | null;
  alert_type: string | null;
  timestamp: string;
  when_ago_minutes: number | null;
  tags: string[];
}

export interface RecentEventsContext {
  from_ts: string;
  to_ts: string;
  query: string;
  total: number;
  events: RecentEvent[];
}

export interface LogSummaryServiceBucket {
  name: string;
  total: number;
  error_kinds: {
    kind: string;
    count: number;
    samples: { timestamp: string; message: string; host: string | null; trace_id: string | null; status: string | null }[];
  }[];
}

export interface LogSummaryContext {
  from_ts: string;
  to_ts: string;
  filter_query: string;
  total: number;
  services: LogSummaryServiceBucket[];
}

export interface AnalysisContext {
  trace?: { args: Record<string, unknown>; result: TraceDetail };
  recent_events?: { args: Record<string, unknown>; result: RecentEventsContext };
  log_summary?: { args: Record<string, unknown>; result: LogSummaryContext };
  candidate_traces?: { args: Record<string, unknown>; result: { traces: { trace_id: string }[] } };
}

export interface AnalysisResult {
  id: string;
  issue_id: string;
  severity: Severity;
  severity_score: number;
  summary: string;
  impact: string;
  action: AnalysisAction;
  fix_snippet: string | null;
  related_services: string[];
  confidence: number;
  model: string;
  prompt_version: string;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  latency_ms: number;
  completed_at: string;       // ISO 8601 — parse to Date only for display
  // Agentic-only fields (absent for static-prompt analyses)
  tool_calls_log: ToolCallEntry[];
  rounds: number;
  sources_used: string[];     // e.g. ['sentry.event', 'github.read_file', 'datadog.logs']
  context: AnalysisContext;
}

export interface AnalysisInFlight {
  status: 'queued' | 'running';
  // Agentic-only fields — null for static prompt or before the first tool call
  current_round: number | null;
  current_tool: string | null;
}

/**
 * Mirror of backend AnalysisFailed schema. Surfaced when the backing analysis
 * row's status is 'failed'. Carries the persisted error_message plus the
 * agentic-loop progress captured before the failure.
 */
export interface AnalysisFailed {
  status: 'failed';
  error_message: string;
  current_round: number | null;
  current_tool: string | null;
  rounds: number;
  tool_calls_log: ToolCallEntry[];
}

export type AnalysisGetResponse = AnalysisResult | AnalysisInFlight | AnalysisFailed;

export interface AnalysisQueued {
  status: 'queued';
  task_id: string;
  estimated_cost_usd: number;
  estimated_latency_seconds: number;
}

/**
 * Type guard: returns true when the response is a completed AnalysisResult (not queued/running/failed).
 * Relies on `severity` being present only on AnalysisResult.
 */
export function isAnalysisResult(r: AnalysisGetResponse): r is AnalysisResult {
  return 'severity' in r && (r as { status?: string }).status !== 'failed';
}

/**
 * Type guard: returns true when the response is a terminal AnalysisFailed.
 * Used by the polling hook to stop the interval and render the existing
 * failed branch in IssueDetailPage.
 */
export function isAnalysisFailed(r: AnalysisGetResponse): r is AnalysisFailed {
  return (r as { status?: string }).status === 'failed';
}

// Per-million-token USD pricing — mirrors backend/sherlock/llm/client.py:MODEL_PRICING.
// Keep in sync if Anthropic pricing changes.
const MODEL_PRICING: Record<AnalysisModel, { input: number; output: number }> = {
  'claude-sonnet-4-6': { input: 3.0, output: 15.0 },
  // 'claude-opus-4-7': { input: 15.0, output: 75.0 },  // disabled — see AnalysisModel above
};

// Fixed token estimates for the auto_triage_v1 prompt. Mirrors the backend constants.
const AUTO_TRIAGE_INPUT_TOKENS = 2000;
const AUTO_TRIAGE_OUTPUT_TOKENS = 600;

// Agentic analyses have variable token usage across multiple rounds.
// 3x is a rough placeholder; the POST response provides the actual estimate.
const AGENTIC_COST_MULTIPLIER = 3;

/**
 * Estimate the cost of an analysis request.
 * For agentic prompt versions, apply a 3x multiplier to account for multi-round tool calls.
 */
export function estimateRequestCostUsd(
  model: AnalysisModel,
  promptVersion: PromptVersion = 'auto_triage_agentic.v1',
): number {
  const p = MODEL_PRICING[model];
  const base =
    (AUTO_TRIAGE_INPUT_TOKENS / 1_000_000) * p.input +
    (AUTO_TRIAGE_OUTPUT_TOKENS / 1_000_000) * p.output;
  return promptVersion === 'auto_triage_agentic.v1' ? base * AGENTIC_COST_MULTIPLIER : base;
}
